// apps/client/src/hooks/pipelineTracker/useMovementTrendReport.ts
//
// Pipeline Tracker: deal movements month by month, the 12 calendar months ending at the
// snapshot's month (movementTrend.ts on the server).
//
// Takes the SAME scope as the Pipeline report (broker, book, deal type, label), so the chart
// narrows with every chip on the tab and a broker's line can never be drawn from the company's
// deals. The period selector does not reach it: the chart IS the months.
//
// ONE REFERRER. `partnerKey` narrows the trend to the deals one referrer sent (Leads & Partners,
// single referrer view). It is the only field that can make the server answer
// `needs_sales_export`, so the response type widens only when it is given: a caller without it
// keeps the response type it always had.

import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { REPORT_CALLABLE } from './reportContract';
import { MOVEMENT_TREND_REPORT_ACTION, type MovementTrendResponse } from './targetsContract';
import type { NeedsSalesExportResponse } from './salesAnalyticsTypes';

export interface UseMovementTrendParams {
  companyId: string;
  asAtDate?: string;
  adviserId?: string;
  groupKey?: string;
  workflowName?: string;
  dealLabel?: string;
  /** One referrer's deals only: a `PartnerRow.key` from Leads & Partners. Never sent when absent. */
  partnerKey?: string;
}

/** What the server can answer when a referrer is asked for. */
export type MovementTrendPartnerResponse = MovementTrendResponse | NeedsSalesExportResponse;

export interface UseMovementTrendReturn<R> {
  response: R | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useMovementTrendReport(
  params: UseMovementTrendParams & { partnerKey: string },
): UseMovementTrendReturn<MovementTrendPartnerResponse>;
export function useMovementTrendReport(
  params: Omit<UseMovementTrendParams, 'partnerKey'>,
): UseMovementTrendReturn<MovementTrendResponse>;
export function useMovementTrendReport({
  companyId,
  asAtDate,
  adviserId,
  groupKey,
  workflowName,
  dealLabel,
  partnerKey,
}: UseMovementTrendParams): UseMovementTrendReturn<MovementTrendPartnerResponse> {
  const [response, setResponse] = useState<MovementTrendPartnerResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const call = httpsCallable(functions, REPORT_CALLABLE);
        // Absent fields omitted, never sent as undefined: the transport turns undefined into null,
        // which the server's date check refuses.
        const result = await call({
          action: MOVEMENT_TREND_REPORT_ACTION,
          companyId,
          ...(asAtDate ? { asAtDate } : {}),
          ...(adviserId ? { adviserId } : {}),
          ...(groupKey ? { groupKey } : {}),
          ...(workflowName ? { workflowName } : {}),
          ...(dealLabel ? { dealLabel } : {}),
          ...(partnerKey ? { partnerKey } : {}),
        });
        if (!cancelled) setResponse(result.data as MovementTrendPartnerResponse);
      } catch (err) {
        if (cancelled) return;
        console.error('[PipelineTracker] movementTrendReport failed', err);
        setError('The 12-month trend could not be loaded.');
        setResponse(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [companyId, asAtDate, adviserId, groupKey, workflowName, dealLabel, partnerKey, nonce]);

  return { response, loading, error, reload };
}
